import { forwardRef, memo, useCallback, useMemo } from 'react';
import * as TabsPrimitive from '@radix-ui/react-tabs';

import { cn } from '../../lib/styles';
import { useSegmentedControlAnimation } from './SegmentedControl.utils';
import { Tooltip } from '../Tooltip';

export interface SegmentedControlItem<T extends string> {
  label: React.ReactNode;
  value: T;
  disabled?: boolean;
  tooltip?: React.ReactNode;
}

export interface SegmentedControlProps<T extends string> {
  data: T[] | SegmentedControlItem<T>[];
  value?: T;
  defaultValue?: T;
  onChange?: (value: T) => void;
  disabled?: boolean;
  triggerClassName?: string;
  className?: string;
  transitionDuration?: number;
}

const SegmentedControlBase = <T extends string>(
  {
    data,
    value,
    defaultValue,
    onChange,
    disabled = false,
    triggerClassName,
    className,
    transitionDuration = 200,
    ...rest
  }: SegmentedControlProps<T>,
  ref: React.ForwardedRef<HTMLDivElement>,
) => {
  const normalizedData: SegmentedControlItem<T>[] = useMemo(() => {
    return data.map((item) => {
      if (typeof item === 'string') return { label: item, value: item };

      return item;
    });
  }, [data]);

  const fallbackDefaultValue = defaultValue || normalizedData[0]?.value;
  const currentValue = value || fallbackDefaultValue;

  const { containerRef, indicatorStyle, isAnimating } = useSegmentedControlAnimation({
    currentValue,
    transitionDuration,
  });

  const handleValueChange = useCallback(
    (newValue: string) => {
      onChange?.(newValue as T);
    },
    [onChange],
  );

  return (
    <TabsPrimitive.Root
      ref={ref}
      value={value}
      defaultValue={fallbackDefaultValue}
      onValueChange={handleValueChange}
      className={cn('flex bg-bg-inter-secondary-default h-[34px] w-fit p-0.5 rounded-sm relative', className)}
      {...rest}
    >
      <TabsPrimitive.List
        ref={containerRef}
        className={cn('h-full flex gap-1 relative w-full', disabled && 'opacity-50 pointer-events-none')}
      >
        {indicatorStyle && (
          <div
            className={cn(
              'absolute top-0 bottom-0 bg-bg-sur-primary rounded-sm shadow-sm transition-all ease-out',
              isAnimating ? 'transition-all' : '',
            )}
            style={{
              left: `${indicatorStyle.left}px`,
              width: `${indicatorStyle.width}px`,
              transitionDuration: `${transitionDuration}ms`,
            }}
          />
        )}

        {normalizedData.map((item) => {
          const trigger = (
            <TabsPrimitive.Trigger
              key={item.value}
              value={item.value}
              disabled={disabled || item.disabled}
              className={cn(
                'relative z-10 inline-flex items-center justify-center gap-1.5 rounded-md border border-transparent px-3 transition-all w-full',
                'font-body-md font-medium whitespace-nowrap',
                'cursor-pointer px-4 h-[30px] rounded-sm',
                'data-[state=active]:text-text-secondary',
                'data-[state=inactive]:hover:bg-bg-sur-secondary data-[state=inactive]:text-text-secondary',
                'focus-visible:border-ring focus-visible:ring-blue-300 focus-visible:outline-ring focus-visible:ring-[1px] focus-visible:outline-1',
                'disabled:pointer-events-none disabled:opacity-50',
                triggerClassName,
              )}
            >
              <div className="font-body-sm font-semibold truncate">{item.label}</div>
            </TabsPrimitive.Trigger>
          );

          if (!item.tooltip) return trigger;

          return (
            <Tooltip key={item.value} label={item.tooltip} withinPortal>
              {trigger}
            </Tooltip>
          );
        })}
      </TabsPrimitive.List>
    </TabsPrimitive.Root>
  );
};

type SegmentedControlComponent = <T extends string>(
  props: SegmentedControlProps<T> & React.RefAttributes<HTMLDivElement>,
) => React.ReactElement | null;

export const SegmentedControl = memo(forwardRef(SegmentedControlBase)) as unknown as SegmentedControlComponent;
